import {Button, CTABanner, type CTABannerProps} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import type {PrimerComponentCtaBanner} from '../../../schemas/contentful/contentTypes/primerComponentCtaBanner'
import {getAnalyticsEvent, documentToPlainTextString} from '../../../lib/utils/analytics'
import {getStructuredImageSources, MAX_CONTENT_WIDTH} from '../../../lib/utils/images'

import {ContentfulAppStoreButtonGroup} from '../ContentfulAppStoreButtonGroup/ContentfulAppStoreButtonGroup'
import styles from './ContentfulCtaBanner.module.css'
import {renderSingleLineRichText} from '../shared/RichTextFieldRenderer'
import {getLinkHref} from '../../../lib/utils/links'

export type ContentfulCtaBannerProps = {
  component: PrimerComponentCtaBanner
  className?: string
  backgroundImage?: string
}

export function ContentfulCtaBanner({component, className, backgroundImage}: ContentfulCtaBannerProps) {
  const enableCodeBlocks = isFeatureEnabled('contentful_primer_code_blocks')
  const {colorMode, focus, image} = component.fields?.backgroundImage?.fields || {}
  const hasTrailingComponent = !!component.fields?.trailingComponent?.length
  const backgroundImageUrl = backgroundImage || image?.fields?.file?.url
  const colorModeAttributes = colorMode === 'dark' || colorMode === 'light' ? {'data-color-mode': colorMode} : {}

  let backgroundImageSrc: CTABannerProps['backgroundImageSrc'] = backgroundImageUrl
  if (backgroundImageUrl) {
    backgroundImageSrc = getStructuredImageSources(backgroundImageUrl, {maxWidth: MAX_CONTENT_WIDTH})
  }

  const {callToActionPrimary, callToActionSecondary, heading, headingRich} = component.fields
  const plainTextHeading = heading ?? (headingRich ? documentToPlainTextString(headingRich) : '')

  return (
    <CTABanner
      className={clsx(backgroundImageUrl && styles.withImage, className)}
      align={component.fields.align}
      hasBorder={component.fields.hasBorder}
      hasShadow={component.fields.hasShadow}
      hasBackground={component.fields.hasBackground}
      backgroundColor={component.fields.backgroundColor}
      backgroundImageSrc={backgroundImageSrc}
      backgroundImagePosition={backgroundImageUrl && (focus || 'center')}
      {...colorModeAttributes}
    >
      {component.fields.logo?.fields?.file?.url && (
        <CTABanner.Logo>
          <img src={component.fields.logo.fields.file.url} alt={component.fields.logo.fields.description || ''} />
        </CTABanner.Logo>
      )}
      <CTABanner.Heading as={component.fields.headingLevel} size={component.fields.headingSize || '2'}>
        {component.fields.headingRich
          ? renderSingleLineRichText(component.fields.headingRich, enableCodeBlocks)
          : component.fields.heading}
      </CTABanner.Heading>
      {component.fields.description && (
        <CTABanner.Description variant={backgroundImageUrl ? 'default' : 'muted'}>
          {renderSingleLineRichText(component.fields.description, enableCodeBlocks)}
        </CTABanner.Description>
      )}
      {hasTrailingComponent ? (
        <ContentfulAppStoreButtonGroup
          components={component.fields.trailingComponent}
          analyticsLabel="cta-banner"
          analyticsLocation={plainTextHeading}
        />
      ) : (
        <CTABanner.ButtonGroup>
          {callToActionPrimary ? (
            <Button
              as="a"
              href={getLinkHref(callToActionPrimary)}
              {...(callToActionPrimary?.fields.openInNewTab && {target: '_blank'})}
              data-vendor-pixel-event=""
              {...getAnalyticsEvent(
                {
                  action: callToActionPrimary?.fields.text || 'Primary CTA',
                  tag: 'button',
                  location: plainTextHeading,
                  context: 'CTAs',
                },
                {context: false},
              )}
            >
              {callToActionPrimary.fields.text}
            </Button>
          ) : (
            <></>
          )}

          {callToActionSecondary ? (
            <Button
              as="a"
              href={getLinkHref(callToActionSecondary)}
              {...(callToActionSecondary.fields.openInNewTab && {target: '_blank'})}
              {...getAnalyticsEvent(
                {
                  action: callToActionSecondary.fields.text || 'Secondary CTA',
                  tag: 'button',
                  location: plainTextHeading,
                  context: 'CTAs',
                },
                {context: false},
              )}
              data-ref={`cta-banner-secondary-action-${callToActionSecondary.sys.id}`}
            >
              {callToActionSecondary.fields.text}
            </Button>
          ) : (
            <></>
          )}
        </CTABanner.ButtonGroup>
      )}
      {component.fields.callToActionLink ? (
        <CTABanner.Link
          href={getLinkHref(component.fields.callToActionLink)}
          {...(component.fields.callToActionLink.fields.openInNewTab && {target: '_blank'})}
          {...getAnalyticsEvent(
            {
              action: component.fields.callToActionLink.fields.text || 'CTA Link',
              tag: 'link',
              location: plainTextHeading,
              context: 'CTAs',
            },
            {context: false},
          )}
        >
          {component.fields.callToActionLink.fields.text}
        </CTABanner.Link>
      ) : null}
    </CTABanner>
  )
}
